import { useState } from 'react';
import { ExternalLink, LogOut, User as UserIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useClerk } from '../../services/clerkReact';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';

interface ProfileMenuProps {
  /** Rail/collapsed mode hides the name + plan tier; only the avatar trigger renders. */
  collapsed?: boolean;
}

export default function ProfileMenu({ collapsed = false }: ProfileMenuProps) {
  const clerk = useClerk();
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const landingTarget = (import.meta.env.VITE_LANDING_URL ?? '/').replace(/\/+$/, '') || '/';

  const user = clerk.user;
  const userInitial =
    user?.firstName?.[0] || user?.emailAddresses?.[0]?.emailAddress?.[0]?.toUpperCase() || 'U';
  const userName = user?.fullName || user?.firstName || 'User';
  const userEmail = user?.emailAddresses?.[0]?.emailAddress || '';
  const planLabel = 'Solopreneur · Pro';

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await clerk.signOut({ redirectUrl: landingTarget });
    } catch (error) {
      console.error('Failed to sign out', error);
    } finally {
      if (landingTarget.startsWith('http')) {
        window.location.href = landingTarget;
      } else {
        navigate(landingTarget || '/', { replace: true });
      }
      setIsSigningOut(false);
    }
  };

  const handleManageAccount = () => {
    setIsProfileOpen(false);
    if (clerk.openUserProfile) {
      clerk.openUserProfile();
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={isSigningOut}
            aria-label="Open account menu"
            className={cn(
              'flex w-full items-center rounded-md p-1.5 text-left transition-colors hover:bg-bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
              collapsed ? 'justify-center' : 'gap-2.5',
            )}
          >
            <span
              aria-hidden
              className={cn(
                'flex items-center justify-center rounded-pill bg-terracotta font-semibold text-fg-inverse flex-shrink-0',
                collapsed ? 'h-7 w-7 text-[0.75rem]' : 'h-9 w-9 text-small',
              )}
            >
              {userInitial}
            </span>
            {!collapsed && (
              <span className="min-w-0 flex-1 overflow-hidden">
                <span className="block truncate text-small font-semibold text-fg-primary">
                  {userName}
                </span>
                <span className="block truncate text-[0.75rem] text-fg-secondary">
                  {planLabel}
                </span>
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side={collapsed ? 'right' : 'top'}
          align="start"
          sideOffset={8}
          className="w-64 border border-border bg-bg-primary text-fg-primary shadow-md"
        >
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col gap-0.5">
              <span className="text-small font-semibold text-fg-primary">{userName}</span>
              {userEmail && (
                <span className="truncate text-[0.75rem] text-fg-secondary">{userEmail}</span>
              )}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => setIsProfileOpen(true)}
            className="gap-2 text-small text-fg-primary focus:bg-bg-secondary focus:text-fg-primary"
          >
            <UserIcon className="h-4 w-4 text-fg-secondary" aria-hidden />
            Profile
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={isSigningOut}
            onSelect={handleSignOut}
            className="gap-2 text-small text-fg-primary focus:bg-bg-secondary focus:text-fg-primary"
          >
            <LogOut className="h-4 w-4 text-fg-secondary" aria-hidden />
            {isSigningOut ? 'Signing out…' : 'Sign out'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isProfileOpen} onOpenChange={setIsProfileOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div
                aria-hidden
                className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-pill bg-terracotta text-h4 font-semibold text-fg-inverse"
              >
                {userInitial}
              </div>
              <div className="min-w-0">
                <p className="truncate text-h5 font-semibold text-fg-primary">{userName}</p>
                {userEmail && (
                  <p className="truncate text-small text-fg-secondary">{userEmail}</p>
                )}
              </div>
            </div>

            <div className="space-y-2 rounded-lg border border-border bg-bg-secondary px-4 py-3">
              <div className="flex justify-between gap-4 text-small">
                <span className="text-fg-secondary">Name</span>
                <span className="font-medium text-fg-primary">{userName}</span>
              </div>
              {userEmail && (
                <div className="flex justify-between gap-4 text-small">
                  <span className="text-fg-secondary">Email</span>
                  <span className="truncate font-medium text-fg-primary">{userEmail}</span>
                </div>
              )}
              <div className="flex justify-between gap-4 text-small">
                <span className="text-fg-secondary">Plan</span>
                <span className="font-medium text-fg-primary">{planLabel}</span>
              </div>
            </div>

            <Button onClick={handleManageAccount} className="w-full">
              Manage account
              <ExternalLink className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
